<script setup lang="ts">
import { Check, MessageSquare, RotateCcw, Trash2 } from 'lucide-vue-next';
import { computed, nextTick, ref, watch } from 'vue';
import EmptyState from '@/components/shared/EmptyState.vue';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { DocumentCommentThreadItem } from './types';

const props = defineProps<{
    threads: DocumentCommentThreadItem[];
    currentUserId: string;
    /** Set (non-null) right after the user selects text and clicks "Commenter" — shows the new-thread composer pinned at the top. */
    pendingAnchor: string | null;
    /** Set when a highlight in the document is clicked — scrolls to and briefly highlights that thread. */
    focusThreadId: string | null;
}>();

const emit = defineEmits<{
    createThread: [body: string];
    cancelPending: [];
    reply: [threadId: string, body: string];
    resolve: [threadId: string];
    reopen: [threadId: string];
    remove: [threadId: string];
}>();

// ── New-thread composer ─────────────────────────────────────────────────
const newThreadDraft = ref('');

watch(
    () => props.pendingAnchor,
    (anchor) => {
        if (anchor) {
            newThreadDraft.value = '';
        }
    },
);

function submitNewThread() {
    const body = newThreadDraft.value.trim();

    if (!body) {
        return;
    }

    emit('createThread', body);
    newThreadDraft.value = '';
}

// ── Reply drafts (one per thread) ───────────────────────────────────────
const replyDrafts = ref<Record<string, string>>({});

function submitReply(threadId: string) {
    const body = (replyDrafts.value[threadId] ?? '').trim();

    if (!body) {
        return;
    }

    emit('reply', threadId, body);
    replyDrafts.value[threadId] = '';
}

// ── Open / resolved split ────────────────────────────────────────────────
const openThreads = computed(() => props.threads.filter((t) => !t.resolved_at));
const resolvedThreads = computed(() =>
    props.threads.filter((t) => t.resolved_at),
);
const showResolved = ref(false);

// ── Scroll to / flash a thread opened from a highlight click ───────────
const threadRefs = ref<Record<string, HTMLElement | null>>({});

watch(
    () => props.focusThreadId,
    async (id) => {
        if (!id) {
            return;
        }

        // The thread might be in the (collapsed) resolved section.
        if (props.threads.find((t) => t.id === id)?.resolved_at) {
            showResolved.value = true;
        }

        await nextTick();
        threadRefs.value[id]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    },
);

function formatDate(iso: string): string {
    return new Date(iso).toLocaleString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}
</script>

<template>
    <div class="flex h-full flex-col">
        <div class="flex-1 space-y-3 p-4">
            <!-- New-thread composer, pinned above the list while a selection is pending -->
            <div
                v-if="props.pendingAnchor !== null"
                class="rounded-lg border border-brand/40 bg-brand/5 p-3"
            >
                <p
                    class="mb-1.5 line-clamp-2 border-l-2 border-brand/50 pl-2 text-xs text-muted-foreground italic"
                >
                    {{ props.pendingAnchor || '(sélection vide)' }}
                </p>
                <Textarea
                    v-model="newThreadDraft"
                    autofocus
                    placeholder="Votre commentaire…"
                    class="min-h-16 resize-none bg-background text-sm"
                    @keydown.enter.meta.prevent="submitNewThread"
                    @keydown.enter.ctrl.prevent="submitNewThread"
                />
                <div class="mt-2 flex items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        class="h-7 px-2 text-xs"
                        @click="emit('cancelPending')"
                    >
                        Annuler
                    </Button>
                    <Button
                        size="sm"
                        class="h-7 px-3 text-xs"
                        :disabled="!newThreadDraft.trim()"
                        @click="submitNewThread"
                    >
                        Commenter
                    </Button>
                </div>
            </div>

            <EmptyState
                v-if="
                    props.threads.length === 0 && props.pendingAnchor === null
                "
                :icon="MessageSquare"
                title="Aucun commentaire"
                description="Sélectionnez du texte dans le document puis cliquez sur « Commenter » pour en ajouter un."
                class="border-border/40"
            />

            <!-- Open threads -->
            <div
                v-for="thread in openThreads"
                :key="thread.id"
                :ref="
                    (el) => (threadRefs[thread.id] = el as HTMLElement | null)
                "
                class="rounded-lg border border-border/50 bg-muted/20 p-3 transition-colors"
                :class="{
                    'border-brand/60 ring-1 ring-brand/40':
                        thread.id === props.focusThreadId,
                }"
            >
                <p
                    v-if="thread.anchor_text"
                    class="mb-2 line-clamp-2 border-l-2 border-border/60 pl-2 text-xs text-muted-foreground italic"
                >
                    {{ thread.anchor_text }}
                </p>

                <div
                    v-for="comment in thread.comments"
                    :key="comment.id"
                    class="mb-2 last:mb-0"
                >
                    <div class="text-xs text-muted-foreground">
                        <span class="font-medium text-foreground">{{
                            comment.author.name
                        }}</span>
                        · {{ formatDate(comment.created_at) }}
                    </div>
                    <p class="text-sm whitespace-pre-line text-foreground">
                        {{ comment.body }}
                    </p>
                </div>

                <Textarea
                    v-model="replyDrafts[thread.id]"
                    placeholder="Répondre…"
                    class="mt-2 min-h-10 resize-none bg-background text-xs"
                    @keydown.enter.meta.prevent="submitReply(thread.id)"
                    @keydown.enter.ctrl.prevent="submitReply(thread.id)"
                />

                <div class="mt-2 flex items-center justify-between">
                    <Button
                        variant="ghost"
                        size="sm"
                        class="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                        @click="emit('resolve', thread.id)"
                    >
                        <Check class="h-3 w-3" />
                        Résoudre
                    </Button>
                    <div class="flex items-center gap-1">
                        <Button
                            v-if="!replyDrafts[thread.id]?.trim()"
                            size="sm"
                            variant="ghost"
                            class="h-6 w-6 p-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            title="Supprimer le fil"
                            @click="emit('remove', thread.id)"
                        >
                            <Trash2 class="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            v-else
                            size="sm"
                            class="h-6 px-2 text-[11px]"
                            @click="submitReply(thread.id)"
                        >
                            Répondre
                        </Button>
                    </div>
                </div>
            </div>

            <!-- Resolved threads, collapsed by default -->
            <div v-if="resolvedThreads.length > 0">
                <button
                    class="flex w-full items-center gap-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase"
                    @click="showResolved = !showResolved"
                >
                    Résolus ({{ resolvedThreads.length }})
                </button>

                <div v-if="showResolved" class="mt-1.5 space-y-2">
                    <div
                        v-for="thread in resolvedThreads"
                        :key="thread.id"
                        :ref="
                            (el) =>
                                (threadRefs[thread.id] =
                                    el as HTMLElement | null)
                        "
                        class="rounded-lg border border-border/30 p-3 opacity-70 transition-colors"
                        :class="{
                            'border-brand/60 opacity-100 ring-1 ring-brand/40':
                                thread.id === props.focusThreadId,
                        }"
                    >
                        <p
                            v-if="thread.anchor_text"
                            class="mb-2 line-clamp-2 border-l-2 border-border/60 pl-2 text-xs text-muted-foreground italic"
                        >
                            {{ thread.anchor_text }}
                        </p>
                        <div
                            v-for="comment in thread.comments"
                            :key="comment.id"
                            class="mb-2 last:mb-0"
                        >
                            <div class="text-xs text-muted-foreground">
                                <span class="font-medium text-foreground">{{
                                    comment.author.name
                                }}</span>
                                · {{ formatDate(comment.created_at) }}
                            </div>
                            <p
                                class="text-sm whitespace-pre-line text-foreground"
                            >
                                {{ comment.body }}
                            </p>
                        </div>
                        <p class="mt-1 text-[11px] text-muted-foreground">
                            Résolu par {{ thread.resolved_by?.name }}
                        </p>
                        <div class="mt-2 flex items-center justify-between">
                            <Button
                                variant="ghost"
                                size="sm"
                                class="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                                @click="emit('reopen', thread.id)"
                            >
                                <RotateCcw class="h-3 w-3" />
                                Rouvrir
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                class="h-6 w-6 p-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                title="Supprimer le fil"
                                @click="emit('remove', thread.id)"
                            >
                                <Trash2 class="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
